import * as XLSX from 'xlsx';
import { Participant, Activity } from '../models/types';

/**
 * Converts an Excel date number to a JavaScript Date object
 * @param excelDate The Excel date number
 * @returns A JavaScript Date object
 */
export function parseExcelDate(excelDate: number): Date {
  // Excel dates are number of days since 1900-01-01
  // JavaScript dates are milliseconds since 1970-01-01
  // Need to adjust for Excel's leap year bug (1900 was not a leap year)
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const excelEpoch = new Date(1900, 0, 1);
  const daysSinceEpoch = excelDate - (excelDate > 60 ? 1 : 0); // Adjust for Excel's leap year bug
  const millisecondsSinceEpoch = daysSinceEpoch * millisecondsPerDay;
  
  // Erstelle das Datum
  const date = new Date(excelEpoch.getTime() + millisecondsSinceEpoch);
  
  // Korrigiere das Datum um einen Tag zurück, da es um einen Tag verschoben ist
  date.setDate(date.getDate() - 1);
  
  return date;
}

/**
 * Gets the value of a cell in a worksheet
 * @param sheet The worksheet
 * @param address The cell address (e.g., 'A1')
 * @returns The cell value
 */
export function getCellValue(sheet: XLSX.WorkSheet, address: string): any {
  const cell = sheet[address];
  return cell ? cell.v : undefined;
}

/**
 * Gets the value of a cell and converts it to a date if it's a number
 * @param sheet The worksheet
 * @param address The cell address
 * @returns The cell value as a date (if applicable) or the original value
 */
export function getCellDateValue(sheet: XLSX.WorkSheet, address: string): Date | any {
  const value = getCellValue(sheet, address);
  if (typeof value === 'number') {
    return parseExcelDate(value);
  }
  return value;
}

/**
 * Finds a cell containing the specified text
 * @param sheet The worksheet
 * @param searchText The text to search for
 * @returns The cell address or undefined if not found
 */
export function findCellWithText(sheet: XLSX.WorkSheet, searchText: string): string | undefined {
  const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
  
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cellValue = getCellValue(sheet, cellAddress);
      
      if (cellValue && cellValue.toString().includes(searchText)) {
        return cellAddress;
      }
    }
  }
  
  return undefined;
}

/**
 * Extracts participants from a range in the worksheet
 * @param sheet The worksheet
 * @param startCell The starting cell of the range
 * @param numRows The number of rows to extract
 * @returns Array of participants
 */
export function extractParticipants(sheet: XLSX.WorkSheet, startCell: string, numRows: number): Participant[] {
  const participants: Participant[] = [];
  const { r: startRow, c: startCol } = XLSX.utils.decode_cell(startCell);
  
  for (let i = 0; i < numRows; i++) {
    const row = startRow + i;
    const nameCell = XLSX.utils.encode_cell({ r: row, c: startCol });
    const departmentCell = XLSX.utils.encode_cell({ r: row, c: startCol + 1 });
    const roleCell = XLSX.utils.encode_cell({ r: row, c: startCol + 2 });
    
    const name = getCellValue(sheet, nameCell);
    if (name) {
      const participant: Participant = {
        name: name.toString(),
        department: getCellValue(sheet, departmentCell)?.toString(),
        role: getCellValue(sheet, roleCell)?.toString()
      };
      
      // Try to extract email from name if it contains '@'
      if (name.toString().includes('@')) {
        participant.email = name.toString();
      } else if (name.toString().includes('<') && name.toString().includes('>')) {
        // Extract email from format "Name <email@example.com>"
        const match = name.toString().match(/<([^>]+)>/);
        if (match && match[1]) {
          participant.email = match[1];
        }
      }
      
      participants.push(participant);
    }
  }
  
  return participants;
}

/**
 * Extracts activities from a range in the worksheet
 * @param sheet The worksheet
 * @param startCell The starting cell of the range
 * @param numRows The number of rows to extract
 * @returns Array of activities
 */
export function extractActivities(sheet: XLSX.WorkSheet, startCell: string, numRows: number): Activity[] {
  const activities: Activity[] = [];
  const { r: startRow, c: startCol } = XLSX.utils.decode_cell(startCell);
  
  for (let i = 0; i < numRows; i++) {
    const row = startRow + i;
    const titleCell = XLSX.utils.encode_cell({ r: row, c: startCol });
    const startDateCell = XLSX.utils.encode_cell({ r: row, c: startCol + 1 });
    const endDateCell = XLSX.utils.encode_cell({ r: row, c: startCol + 2 });
    const descriptionCell = XLSX.utils.encode_cell({ r: row, c: startCol + 3 });
    
    const title = getCellValue(sheet, titleCell);
    const startDate = getCellDateValue(sheet, startDateCell);
    
    if (title && startDate instanceof Date) {
      const activity: Activity = {
        title: title.toString(),
        startDate,
        description: getCellValue(sheet, descriptionCell)?.toString()
      };
      
      const endDate = getCellDateValue(sheet, endDateCell);
      if (endDate instanceof Date) {
        activity.endDate = endDate;
      }
      
      activities.push(activity);
    }
  }
  
  return activities;
}
